import React, { useEffect, useState } from "react";

import { useDispatch } from "react-redux";

import { Link, useNavigate } from "react-router-dom";

import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaLock,
  FaHome,
  FaMapMarkedAlt,
  FaCity,
  FaFlag,
  FaCamera,
  FaCalendarAlt,
  FaHeartbeat,
  FaUserPlus,
  FaArrowRight,
  FaShieldAlt,
} from "react-icons/fa";

import registerUser from "../../redux/apis/registerUser";

// ============================================================
// COLORS / GRADIENTS
// ============================================================

const BUTTON_GRADIENT =
  "linear-gradient(135deg, rgb(26, 58, 92), rgb(14, 165, 233))";

const MAIN_GRADIENT =
  "linear-gradient(135deg, #eff6ff 0%, #ffffff 48%, #e0f2fe 100%)";

const PANEL_GRADIENT =
  "linear-gradient(135deg, rgb(26, 58, 92) 0%, rgb(14, 116, 144) 55%, rgb(14, 165, 233) 100%)";

// ============================================================
// INPUT FIELD
// ============================================================

const InputField = ({
  label,
  name,
  type = "text",
  value,
  onChange,
  placeholder,
  icon: Icon,
  hint,
  error,
  required = true,
}) => {
  return (
    <div className="w-full min-w-0">
      <label
        htmlFor={name}
        className="mb-1.5 flex items-center gap-1 text-sm font-semibold text-slate-700"
      >
        {label}

        {required && (
          <span className="text-red-500">
            *
          </span>
        )}
      </label>

      <div
        className={`
          flex
          w-full
          min-w-0
          items-center
          rounded-xl
          border
          bg-white
          transition-all
          duration-200
          ${
            error
              ? "border-red-500 ring-1 ring-red-200"
              : "border-slate-200 focus-within:border-sky-500 focus-within:ring-2 focus-within:ring-sky-100"
          }
        `}
      >
        {Icon && (
          <Icon className="ml-3 shrink-0 text-slate-400" />
        )}

        <input
          id={name}
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="
            min-w-0
            w-full
            bg-transparent
            px-3
            py-3
            text-sm
            text-slate-800
            placeholder-slate-400
            outline-none
            sm:text-base
          "
        />
      </div>

      {hint && !error && (
        <p className="mt-1 text-xs text-slate-500">
          {hint}
        </p>
      )}

      {error && (
        <p className="mt-1 text-xs text-red-500">
          {error}
        </p>
      )}
    </div>
  );
};

// ============================================================
// SELECT FIELD
// ============================================================

const SelectField = ({
  label,
  name,
  value,
  onChange,
  options,
  error,
}) => {
  return (
    <div className="w-full min-w-0">
      <label
        htmlFor={name}
        className="mb-1.5 flex items-center gap-1 text-sm font-semibold text-slate-700"
      >
        {label}

        <span className="text-red-500">
          *
        </span>
      </label>

      <div
        className={`
          w-full
          rounded-xl
          border
          bg-white
          transition-all
          duration-200
          ${
            error
              ? "border-red-500 ring-1 ring-red-200"
              : "border-slate-200 focus-within:border-sky-500 focus-within:ring-2 focus-within:ring-sky-100"
          }
        `}
      >
        <select
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          className="
            w-full
            cursor-pointer
            rounded-xl
            bg-transparent
            px-3
            py-3
            text-sm
            text-slate-800
            outline-none
            sm:text-base
          "
        >
          <option value="">
            Select Gender
          </option>

          {options.map((opt) => (
            <option
              key={opt}
              value={opt}
            >
              {opt.charAt(0).toUpperCase() +
                opt.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <p className="mt-1 text-xs text-red-500">
          {error}
        </p>
      )}
    </div>
  );
};

// ============================================================
// MAIN SIGNUP PAGE
// ============================================================

const SignupPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ==========================================================
  // FORM DATA
  // ==========================================================

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    contact: "",
    username: "",
    password: "",
    confirmPassword: "",
    village: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
    profilePic: null,
    dob: "",
    gender: "",
  });

  const [error, setError] = useState(null);

  const [fieldErrors, setFieldErrors] =
    useState({});

  // ==========================================================
  // PROFILE PREVIEW
  // ==========================================================

  const [profilePreview, setProfilePreview] =
    useState(null);

  // ==========================================================
  // HANDLE CHANGE
  // ==========================================================

  const handleChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setFieldErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    setError(null);
  };

  // ==========================================================
  // PROFILE IMAGE
  // ==========================================================

  const handleProfilePicChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setFieldErrors((prev) => ({
        ...prev,
        profilePic:
          "Please select a valid image file.",
      }));

      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setFieldErrors((prev) => ({
        ...prev,
        profilePic:
          "Image size must be less than 5 MB.",
      }));

      return;
    }

    setFormData((prev) => ({
      ...prev,
      profilePic: file,
    }));

    setFieldErrors((prev) => ({
      ...prev,
      profilePic: "",
    }));

    setError(null);
  };

  // ==========================================================
  // PROFILE PREVIEW EFFECT
  // ==========================================================

  useEffect(() => {
    if (!formData.profilePic) {
      setProfilePreview(null);
      return;
    }

    const objectUrl =
      URL.createObjectURL(
        formData.profilePic
      );

    setProfilePreview(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [formData.profilePic]);

  // ==========================================================
  // SUBMIT
  // ==========================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError(null);
    setFieldErrors({});

    const errors = {};

    // ========================================================
    // NAME
    // ========================================================

    if (
      !formData.name ||
      formData.name.trim().length < 3
    ) {
      errors.name =
        "Minimum 3 characters required";
    }

    // ========================================================
    // EMAIL
    // ========================================================

    if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
        formData.email
      )
    ) {
      errors.email =
        "Enter a valid email address";
    }

    // ========================================================
    // CONTACT
    // ========================================================

    if (
      !/^[6-9]\d{9}$/.test(
        formData.contact
      )
    ) {
      errors.contact =
        "Enter a valid 10-digit phone number";
    }

    // ========================================================
    // USERNAME
    // ========================================================

    if (
      formData.username.trim().length < 4
    ) {
      errors.username =
        "Minimum 4 characters required";
    }

    // ========================================================
    // PASSWORD
    // ========================================================

    if (formData.password.length < 6) {
      errors.password =
        "Minimum 6 characters required";
    }

    // ========================================================
    // CONFIRM PASSWORD
    // ========================================================

    if (
      formData.password !==
      formData.confirmPassword
    ) {
      errors.confirmPassword =
        "Passwords do not match";
    }

    // ========================================================
    // GENDER
    // ========================================================

    if (!formData.gender) {
      errors.gender =
        "Please select gender";
    }

    // ========================================================
    // POSTAL CODE
    // ========================================================

    if (
      !/^[1-9][0-9]{5}$/.test(
        formData.postalCode
      )
    ) {
      errors.postalCode =
        "Enter a valid 6-digit PIN code";
    }

    // ========================================================
    // PROFILE IMAGE
    // ========================================================

    if (!formData.profilePic) {
      errors.profilePic =
        "Profile image is required";
    }

    // ========================================================
    // DOB
    // ========================================================

    if (!formData.dob) {
      errors.dob =
        "Date of birth is required";
    } else {
      const today = new Date();

      const birthDate =
        new Date(formData.dob);

      let age =
        today.getFullYear() -
        birthDate.getFullYear();

      const month =
        today.getMonth() -
        birthDate.getMonth();

      if (
        month < 0 ||
        (month === 0 &&
          today.getDate() <
            birthDate.getDate())
      ) {
        age--;
      }

      if (age < 18) {
        errors.dob =
          "You must be at least 18 years old";
      }
    }

    // ========================================================
    // VALIDATION
    // ========================================================

    if (
      Object.keys(errors).length > 0
    ) {
      setFieldErrors(errors);
      return;
    }

    // ========================================================
    // API
    // ========================================================

    try {
      const formDataToSend =
        new FormData();

      Object.keys(formData).forEach(
        (key) => {
          formDataToSend.append(
            key,
            formData[key]
          );
        }
      );

      await dispatch(
        registerUser(formDataToSend)
      ).unwrap();

      navigate("/login", {
        state: {
          success:
            "Registered successfully!",
        },
      });
    } catch (err) {
      console.log(
        "FRONTEND ERROR:",
        err
      );

      if (err?.errors) {
        setFieldErrors(
          err.errors
        );
      } else {
        setError(
          err?.message ||
            "Something went wrong. Please try again."
        );
      }
    }
  };

  // ==========================================================
  // CLEAR FORM
  // ==========================================================

  const handleClear = () => {
    setFormData({
      name: "",
      email: "",
      contact: "",
      username: "",
      password: "",
      confirmPassword: "",
      village: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
      profilePic: null,
      dob: "",
      gender: "",
    });

    setFieldErrors({});
    setError(null);
  };

  // ==========================================================
  // UI
  // ==========================================================

  return (
    <div
      className="
        min-h-screen
        w-full
        overflow-x-hidden
        lg:h-screen
        lg:overflow-hidden
      "
      style={{
        background: MAIN_GRADIENT,
      }}
    >
      {/* =====================================================
          PAGE CONTAINER
      ===================================================== */}

      <div
        className="
          mx-auto
          flex
          w-full
          max-w-7xl
          flex-col
          px-3
          py-4
          sm:px-5
          sm:py-5
          md:px-6
          lg:h-full
          lg:px-8
          lg:py-4
        "
      >
        {/* ===================================================
            MAIN CARD
        =================================================== */}

        <div
          className="
            relative
            mx-auto
            flex
            w-full
            max-w-6xl
            min-h-0
            flex-1
            overflow-hidden
            rounded-2xl
            border
            border-sky-100
            bg-white
            shadow-xl
            sm:rounded-3xl
          "
        >
          {/* =================================================
              TOP LEFT DECORATION
          ================================================= */}

          <div
            className="
              pointer-events-none
              absolute
              -left-16
              -top-16
              z-0
              h-32
              w-32
              rounded-full
              opacity-80
              sm:-left-20
              sm:-top-20
              sm:h-40
              sm:w-40
            "
            style={{
              background:
                "linear-gradient(135deg, rgba(14,165,233,0.25), rgba(26,58,92,0.10))",
            }}
          />

          {/* =================================================
              TOP RIGHT DECORATION
          ================================================= */}

          <div
            className="
              pointer-events-none
              absolute
              -right-16
              -top-16
              z-0
              h-32
              w-32
              rounded-full
              opacity-80
              sm:-right-20
              sm:-top-20
              sm:h-40
              sm:w-40
            "
            style={{
              background:
                "linear-gradient(135deg, rgba(14,165,233,0.12), rgba(14,116,144,0.25))",
            }}
          />

          {/* =================================================
              BOTTOM LEFT DECORATION
          ================================================= */}

          <div
            className="
              pointer-events-none
              absolute
              -bottom-20
              -left-20
              z-0
              h-40
              w-40
              rounded-full
              opacity-70
              sm:-bottom-24
              sm:-left-24
              sm:h-48
              sm:w-48
            "
            style={{
              background:
                "linear-gradient(135deg, rgba(14,165,233,0.20), rgba(26,58,92,0.08))",
            }}
          />

          {/* =================================================
              BOTTOM RIGHT DECORATION
          ================================================= */}

          <div
            className="
              pointer-events-none
              absolute
              -bottom-20
              -right-20
              z-0
              h-40
              w-40
              rounded-full
              opacity-70
              sm:-bottom-24
              sm:-right-24
              sm:h-48
              sm:w-48
            "
            style={{
              background:
                "linear-gradient(135deg, rgba(14,116,144,0.15), rgba(14,165,233,0.25))",
            }}
          />

          {/* =================================================
              MAIN CONTENT
          ================================================= */}

          <div
            className="
              relative
              z-10
              grid
              min-h-0
              flex-1
              grid-cols-1
              lg:grid-cols-5
            "
          >
            {/* =================================================
                LEFT INFORMATION PANEL
            ================================================= */}

            <div
              className="
                hidden
                min-h-0
                lg:col-span-2
                lg:flex
                lg:flex-col
                lg:justify-center
                lg:overflow-hidden
                lg:p-8
                lg:text-white
                xl:p-10
              "
              style={{
                background: PANEL_GRADIENT,
              }}
            >
              <div className="w-full max-w-md">

                {/* =================================================
                    MRHEALTH LOGO
                ================================================= */}

                 <button
              type="button"
              onClick={() =>
                navigate("/Home")
              }
              className="
                flex
                shrink-0
                items-center
              "
            >
              <img
                src="/logo.jpeg"
                alt="MR Health"
                title="MR Health"
                className="
                  h-12
                  w-auto
                  object-contain
                  sm:h-14
                  md:h-16
                "
              />
            </button>

                {/* =================================================
                    ICON
                ================================================= */}

                <div
                  className="
                    mb-5
                    flex
                    h-14
                    w-14
                    items-center
                    justify-center
                    rounded-2xl
                    bg-white/15
                  "
                >
                  <FaUserPlus className="text-2xl" />
                </div>

                {/* =================================================
                    TITLE
                ================================================= */}

                <h2
                  className="
                    text-3xl
                    font-bold
                    leading-tight
                    xl:text-4xl
                  "
                >
                  Join mrhealth.co.in
                </h2>

                <p
                  className="
                    mt-4
                    text-base
                    leading-relaxed
                    text-sky-100
                  "
                >
                  Create your account and become
                  part of our digital healthcare
                  platform connecting patients
                  and doctors.
                </p>

                {/* =================================================
                    BENEFITS
                ================================================= */}

                <div className="mt-6 space-y-4">

                  {/* EASY REGISTRATION */}

                  <div className="flex items-start gap-3">

                    <div
                      className="
                        flex
                        h-8
                        w-8
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        bg-white/15
                      "
                    >
                      <FaUserPlus className="text-sm" />
                    </div>

                    <div>
                      <h3 className="font-semibold">
                        Easy Registration
                      </h3>

                      <p className="mt-1 text-sm text-sky-100">
                        Create your healthcare
                        account quickly.
                      </p>
                    </div>
                  </div>

                  {/* SECURE ACCOUNT */}

                  <div className="flex items-start gap-3">

                    <div
                      className="
                        flex
                        h-8
                        w-8
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        bg-white/15
                      "
                    >
                      <FaShieldAlt className="text-sm" />
                    </div>

                    <div>
                      <h3 className="font-semibold">
                        Secure Account
                      </h3>

                      <p className="mt-1 text-sm text-sky-100">
                        Your account is protected
                        through secure login.
                      </p>
                    </div>
                  </div>

                  {/* HEALTHCARE */}

                  <div className="flex items-start gap-3">

                    <div
                      className="
                        flex
                        h-8
                        w-8
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        bg-white/15
                      "
                    >
                      <FaHeartbeat className="text-sm" />
                    </div>

                    <div>
                      <h3 className="font-semibold">
                        Connected Healthcare
                      </h3>

                      <p className="mt-1 text-sm text-sky-100">
                        Connect with healthcare
                        professionals online.
                      </p>
                    </div>
                  </div>
                </div>

                {/* =================================================
                    USERS
                ================================================= */}

                <div
                  className="
                    mt-7
                    rounded-2xl
                    border
                    border-white/20
                    bg-white/10
                    p-4
                  "
                >
                  <p className="text-xs text-sky-100">
                    mrhealth.co.in is designed for
                  </p>

                  <div className="mt-3 flex flex-wrap gap-3">

                    <span
                      className="
                        rounded-full
                        bg-white/15
                        px-3
                        py-1.5
                        text-sm
                      "
                    >
                      👨‍⚕️ Doctors
                    </span>

                    <span
                      className="
                        rounded-full
                        bg-white/15
                        px-3
                        py-1.5
                        text-sm
                      "
                    >
                      🧑‍⚕️ Patients
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* =================================================
                RIGHT FORM
            ================================================= */}

            <div
              className="
                min-h-0
                min-w-0
                lg:col-span-3
                lg:overflow-y-auto
                lg:overscroll-contain
                p-4
                sm:p-6
                md:p-8
                lg:p-8
                xl:p-10
              "
            >
              {/* =================================================
                  FORM HEADER
              ================================================= */}

              <div className="mb-5 sm:mb-6">

                <div className="flex items-center gap-3">

                  <div
                    className="
                      flex
                      h-10
                      w-10
                      shrink-0
                      items-center
                      justify-center
                      rounded-xl
                      text-white
                      shadow-sm
                    "
                    style={{
                      background:
                        BUTTON_GRADIENT,
                    }}
                  >
                    <FaUserPlus />
                  </div>

                  <div className="min-w-0">

                    <h2
                      className="
                        text-2xl
                        font-bold
                        text-slate-900
                        sm:text-3xl
                      "
                    >
                      Create Your Account
                    </h2>

                    <p
                      className="
                        mt-1
                        text-sm
                        text-slate-500
                        sm:text-base
                      "
                    >
                      Fill in your details to register
                      on mrhealth.co.in
                    </p>
                  </div>
                </div>
              </div>

              {/* =================================================
                  GLOBAL ERROR
              ================================================= */}

              {error && (
                <div
                  className="
                    mb-5
                    rounded-xl
                    border
                    border-red-200
                    bg-red-50
                    p-3
                    text-sm
                    text-red-600
                    sm:p-4
                  "
                >
                  {error}
                </div>
              )}

              {/* =================================================
                  FORM
              ================================================= */}

              <form
                onSubmit={handleSubmit}
                className="
                  w-full
                  space-y-5
                  xl:space-y-6
                "
              >
                {/* =================================================
                    PROFILE PHOTO
                ================================================= */}

                <div className="flex flex-col items-center">

                  <label
                    htmlFor="profilePic"
                    className="group cursor-pointer"
                  >
                    <div
                      className="
                        relative
                        h-24
                        w-24
                        overflow-hidden
                        rounded-full
                        border-4
                        border-sky-100
                        bg-slate-50
                        shadow-md
                        transition
                        group-hover:border-sky-400
                        sm:h-28
                        sm:w-28
                      "
                    >
                      {profilePreview ? (
                        <img
                          src={profilePreview}
                          alt="Profile preview"
                          className="
                            h-full
                            w-full
                            object-cover
                          "
                        />
                      ) : (
                        <div
                          className="
                            flex
                            h-full
                            w-full
                            flex-col
                            items-center
                            justify-center
                            text-slate-400
                          "
                        >
                          <FaCamera className="text-xl sm:text-2xl" />

                          <span className="mt-1 text-xs">
                            Upload Photo
                          </span>
                        </div>
                      )}

                      <div
                        className="
                          absolute
                          bottom-0
                          left-0
                          right-0
                          py-1
                          text-center
                          text-[10px]
                          text-white
                        "
                        style={{
                          background:
                            "rgba(14,165,233,0.90)",
                        }}
                      >
                        Change
                      </div>
                    </div>

                    <input
                      id="profilePic"
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={
                        handleProfilePicChange
                      }
                    />
                  </label>

                  <p className="mt-2 text-xs text-slate-500">
                    Profile image is required
                  </p>

                  {fieldErrors.profilePic && (
                    <p className="mt-1 text-center text-xs text-red-500">
                      {fieldErrors.profilePic}
                    </p>
                  )}
                </div>

                {/* =================================================
                    PERSONAL INFORMATION
                ================================================= */}

                <div>

                  <div className="mb-4 flex items-center gap-2">

                    <div
                      className="
                        flex
                        h-8
                        w-8
                        shrink-0
                        items-center
                        justify-center
                        rounded-lg
                        bg-sky-100
                      "
                    >
                      <FaUser className="text-sm text-sky-600" />
                    </div>

                    <h3 className="text-lg font-bold text-slate-800">
                      Personal Information
                    </h3>
                  </div>

                  <div
                    className="
                      grid
                      min-w-0
                      grid-cols-1
                      gap-4
                      md:grid-cols-2
                    "
                  >
                    <InputField
                      label="Full Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      error={fieldErrors.name}
                      icon={FaUser}
                    />

                    <InputField
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email"
                      error={fieldErrors.email}
                      icon={FaEnvelope}
                    />

                    <InputField
                      label="Contact Number"
                      name="contact"
                      value={formData.contact}
                      onChange={handleChange}
                      placeholder="10-digit mobile number"
                      error={fieldErrors.contact}
                      icon={FaPhone}
                    />

                    <InputField
                      label="Date of Birth"
                      name="dob"
                      type="date"
                      value={formData.dob}
                      onChange={handleChange}
                      error={fieldErrors.dob}
                      icon={FaCalendarAlt}
                    />

                    <SelectField
                      label="Gender"
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      options={[
                        "male",
                        "female",
                        "other",
                      ]}
                      error={fieldErrors.gender}
                    />
                  </div>
                </div>

                {/* =================================================
                    ACCOUNT INFORMATION
                ================================================= */}

                <div>

                  <div className="mb-4 flex items-center gap-2">

                    <div
                      className="
                        flex
                        h-8
                        w-8
                        shrink-0
                        items-center
                        justify-center
                        rounded-lg
                        bg-sky-100
                      "
                    >
                      <FaLock className="text-sm text-sky-600" />
                    </div>

                    <h3 className="text-lg font-bold text-slate-800">
                      Account Information
                    </h3>
                  </div>

                  <div
                    className="
                      grid
                      min-w-0
                      grid-cols-1
                      gap-4
                      md:grid-cols-2
                    "
                  >
                    <InputField
                      label="Username"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      placeholder="Create a username"
                      error={fieldErrors.username}
                      icon={FaUser}
                    />

                    <InputField
                      label="Password"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Create a password"
                      error={fieldErrors.password}
                      icon={FaLock}
                      hint="Minimum 6 characters"
                    />

                    <InputField
                      label="Confirm Password"
                      name="confirmPassword"
                      type="password"
                      value={
                        formData.confirmPassword
                      }
                      onChange={handleChange}
                      placeholder="Re-enter your password"
                      error={
                        fieldErrors.confirmPassword
                      }
                      icon={FaLock}
                    />
                  </div>
                </div>

                {/* =================================================
                    ADDRESS INFORMATION
                ================================================= */}

                <div>

                  <div className="mb-4 flex items-center gap-2">

                    <div
                      className="
                        flex
                        h-8
                        w-8
                        shrink-0
                        items-center
                        justify-center
                        rounded-lg
                        bg-sky-100
                      "
                    >
                      <FaHome className="text-sm text-sky-600" />
                    </div>

                    <h3 className="text-lg font-bold text-slate-800">
                      Address Information
                    </h3>
                  </div>

                  <div
                    className="
                      grid
                      min-w-0
                      grid-cols-1
                      gap-4
                      md:grid-cols-2
                    "
                  >
                    <InputField
                      label="Village"
                      name="village"
                      value={formData.village}
                      onChange={handleChange}
                      placeholder="Enter village"
                      icon={FaHome}
                      required={false}
                    />

                    <InputField
                      label="City"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="Enter city"
                      icon={FaCity}
                      required={false}
                    />

                    <InputField
                      label="State"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      placeholder="Enter state"
                      icon={FaMapMarkedAlt}
                      required={false}
                    />

                    <InputField
                      label="Postal Code"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleChange}
                      placeholder="6-digit PIN code"
                      error={
                        fieldErrors.postalCode
                      }
                      icon={FaMapMarkedAlt}
                    />

                    <InputField
                      label="Country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      placeholder="Enter country"
                      icon={FaFlag}
                      required={false}
                    />
                  </div>
                </div>

                {/* =================================================
                    BOTTOM ACTIONS
                ================================================= */}

                <div
                  className="
                    border-t
                    border-slate-100
                    pt-5
                  "
                >
                  <div
                    className="
                      flex
                      flex-col
                      gap-3
                      sm:flex-row
                      sm:items-center
                      sm:justify-between
                    "
                  >
                    {/* CLEAR */}

                    <button
                      type="button"
                      onClick={handleClear}
                      className="
                        w-full
                        rounded-xl
                        border
                        border-slate-200
                        bg-slate-50
                        px-6
                        py-3
                        font-semibold
                        text-slate-700
                        transition
                        hover:bg-slate-100
                        sm:w-auto
                      "
                    >
                      Clear
                    </button>

                    {/* CREATE ACCOUNT */}

                    <button
                      type="submit"
                      className="
                        flex
                        w-full
                        items-center
                        justify-center
                        gap-2
                        rounded-xl
                        px-7
                        py-3
                        font-semibold
                        text-white
                        shadow-md
                        transition
                        hover:opacity-90
                        hover:shadow-lg
                        active:scale-[0.98]
                        sm:w-auto
                      "
                      style={{
                        background:
                          BUTTON_GRADIENT,
                      }}
                    >
                      Create Account

                      <FaArrowRight className="text-sm" />
                    </button>
                  </div>

                  {/* LOGIN */}

                  <div className="mt-5 text-center">

                    <p className="text-sm text-slate-500">
                      Already have an account?{" "}

                      <Link
                        to="/login"
                        className="
                          font-semibold
                          text-sky-600
                          transition
                          hover:text-sky-700
                          hover:underline
                        "
                      >
                        Login here
                      </Link>
                    </p>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* =====================================================
            FOOTER
        ===================================================== */}

       
      </div>
    </div>
  );
};

export default SignupPage;